/**
 * Phase 4A — Allocation / Reservation validation (pure)
 * ─────────────────────────────────────────────────────
 * Helpers that compare allocations against:
 *   • a job's bound JobSpec BOM (per-SKU required quantity)
 *   • current stock availability (on-hand − reserved)
 *
 * All functions are pure and non-blocking: they return shortages /
 * warnings; they never throw. The provider (WarehouseInventoryContext)
 * decides how to surface them (audit field on the allocation, console
 * warning, UI badge).
 *
 * Phase 4A does NOT consume stock; it only reserves. See
 * src/data/inventory/CANONICAL.md.
 */

import type { Job } from "@/data/types";
import type { JobAllocation, StockRecord } from "@/data/WarehouseInventoryContext";
import type { JobSpecBomItem } from "@/data/jobSpec";

/**
 * Per-SKU BOM requirement for a single job, sourced from its bound
 * JobSpec snapshot. Returns an empty map for jobs with no boundJobSpec
 * (legacy / pre-JobSpec jobs); callers can treat missing requirements as
 * "no shortage to detect."
 */
export function getJobBomRequirements(job: Pick<Job, "boundJobSpec">): Map<string, JobSpecBomItem> {
  const out = new Map<string, JobSpecBomItem>();
  const bom = job.boundJobSpec?.snapshot.billOfMaterials;
  if (!bom) return out;
  for (const item of bom) {
    // Sum duplicates defensively; canonical BOMs shouldn't repeat SKUs.
    const existing = out.get(item.sku);
    if (existing) {
      out.set(item.sku, { ...existing, quantity: existing.quantity + item.quantity });
    } else {
      out.set(item.sku, { ...item });
    }
  }
  return out;
}

export interface AllocationCoverageRow {
  sku: string;
  productName: string;
  required: number;
  allocated: number;
  shortBy: number; // max(required − allocated, 0)
  isShort: boolean;
}

/**
 * Per-SKU coverage of allocations vs. BOM for a single job. Allocations
 * with status "Reserved" or "Ready for Install" count toward coverage.
 * SKUs in the BOM with no allocation appear as `allocated: 0, isShort: true`.
 * Allocations against SKUs not in the BOM are intentionally ignored here
 * (they're tracked separately and may be intentional add-ons).
 */
export function getAllocationCoverage(
  job: Pick<Job, "id" | "boundJobSpec">,
  allocations: JobAllocation[],
): AllocationCoverageRow[] {
  const reqs = getJobBomRequirements(job);
  if (reqs.size === 0) return [];
  const jobAllocs = allocations.filter(
    (a) => a.jobId === job.id && (a.status === "Reserved" || a.status === "Ready for Install"),
  );
  const allocBySku = new Map<string, number>();
  for (const a of jobAllocs) {
    allocBySku.set(a.sku, (allocBySku.get(a.sku) ?? 0) + a.quantity);
  }
  const rows: AllocationCoverageRow[] = [];
  for (const [sku, item] of reqs) {
    const allocated = allocBySku.get(sku) ?? 0;
    const shortBy = Math.max(item.quantity - allocated, 0);
    rows.push({
      sku,
      productName: item.name,
      required: item.quantity,
      allocated,
      shortBy,
      isShort: shortBy > 0,
    });
  }
  return rows;
}

export interface AllocationValidation {
  ok: boolean;
  warning?: string;
  available: number;
  requested: number;
}

/**
 * Validate a proposed reservation against current stock. Non-blocking:
 * over-allocation is permitted (warning surfaced) so warehouse users can
 * still record what physically left the shelf, but the audit trail flags
 * it. Per Phase 4A spec: "warn, not crash."
 */
export function validateAllocation(
  sku: string,
  quantity: number,
  stock: StockRecord | undefined,
): AllocationValidation {
  const available = stock?.availableStock ?? 0;
  if (quantity <= 0) {
    return { ok: false, warning: "Allocation quantity must be greater than 0.", available, requested: quantity };
  }
  if (!stock) {
    return {
      ok: true,
      warning: `SKU ${sku} not in canonical stock catalog. Reservation recorded against 0 on-hand.`,
      available,
      requested: quantity,
    };
  }
  if (quantity > available) {
    return {
      ok: true, // non-blocking
      warning: `Requested ${quantity} of ${sku} but only ${available} available (on-hand ${stock.currentStock}, reserved ${stock.reservedStock}). Reservation recorded; available will go negative.`,
      available,
      requested: quantity,
    };
  }
  return { ok: true, available, requested: quantity };
}
